import type { AiPayoutInput } from '../economy/coins'
import { isQuitReason } from '../economy/coins'
import type { Seat } from '../types/state'
import type { PackId } from '../packs/ids'
import type { BanlistLevelReward } from './banlistReward'

/** Above any vs-AI result, including a Master win (`AI_MASTER_WIN_XP`). */
export const PVP_WIN_XP = 24
/** Casual-queue win. Same as an Easy CPU win. A loss, draw, or quit pays 0. */
export const CASUAL_WIN_XP = 1
/** A finished loss or draw. Still above a Master vs-AI win. */
export const PVP_LOSS_XP = 15
export const AI_HARD_WIN_XP = 6
export const AI_EXPERT_WIN_XP = 8
export const AI_MASTER_WIN_XP = 9
export const AI_NORMAL_WIN_XP = 3
export const AI_EASY_WIN_XP = 1
export const AI_LOSS_XP = 2

export type LevelReward = {
  coins: number
  packs: number
}

export type LevelUpGrant = LevelReward & {
  level: number
}

export type AccountLevelSnapshot = {
  xp: number
  level: number
  xpIntoLevel: number
  xpToNext: number
  nextReward: LevelReward | null
}

export type DuelXpResult = AccountLevelSnapshot & {
  /** XP actually added this duel (0 at cap, on quit, or on replay). */
  xpDelta: number
  grants: LevelUpGrant[]
  /** Wallet after level-up coin grants. */
  coins: number
  /** Inventory pack counts when this duel granted a booster. */
  packs?: Record<PackId, number>
  /** Banlist-card picks this duel newly opened. Absent when none. */
  banlistPicks?: number
  /** Track snapshot after this duel opened at least one pick. */
  banlistReward?: BanlistLevelReward
}

/**
 * XP needed while at `level` to reach the next one.
 * There is no cap. A season reset clears the total.
 */
export function xpToReachNext(level: number): number {
  const safe = Math.max(1, Math.floor(level))
  return 40 + 20 * (safe - 1)
}

/**
 * Minimum total XP to stand on `level` (level 1 is 0).
 */
export function totalXpForLevel(level: number): number {
  if (level <= 1) return 0
  const n = Math.floor(level) - 1
  return 40 * n + 10 * n * (n - 1)
}

/**
 * Account level for a total XP value. Unbounded.
 */
export function levelFromXp(xp: number): number {
  const safe = Math.max(0, Math.floor(xp))
  if (safe < 40) return 1
  const n = Math.floor((-30 + Math.sqrt(900 + 40 * safe)) / 20)
  let level = Math.max(1, n + 1)
  while (totalXpForLevel(level + 1) <= safe) level += 1
  while (level > 1 && totalXpForLevel(level) > safe) level -= 1
  return level
}

/**
 * Shop pack granted when the account first reaches `level`.
 * Level 1 is the start; it has no grant. Every later level is one pack.
 */
export function rewardForReachingLevel(level: number): LevelReward {
  if (!Number.isInteger(level) || level < 2) {
    return { coins: 0, packs: 0 }
  }
  return { coins: 0, packs: 1 }
}

/**
 * Grants for every new level in `(fromLevel, toLevel]`.
 */
export function grantsBetween(
  fromLevel: number,
  toLevel: number,
): LevelUpGrant[] {
  const grants: LevelUpGrant[] = []
  const start = Math.max(1, Math.floor(fromLevel))
  const end = Math.max(0, Math.floor(toLevel))
  for (let level = start + 1; level <= end; level++) {
    const reward = rewardForReachingLevel(level)
    if (reward.coins > 0 || reward.packs > 0) {
      grants.push({ level, ...reward })
    }
  }
  return grants
}

/**
 * HUD snapshot derived from total XP.
 */
export function snapshotFromXp(xp: number): AccountLevelSnapshot {
  const safe = Math.max(0, Math.floor(xp))
  const level = levelFromXp(safe)
  return {
    xp: safe,
    level,
    xpIntoLevel: safe - totalXpForLevel(level),
    xpToNext: xpToReachNext(level),
    nextReward: rewardForReachingLevel(level + 1),
  }
}

export const EMPTY_LEVEL_SNAPSHOT: AccountLevelSnapshot = snapshotFromXp(0)

/**
 * PvP XP for one human seat. A quitting loser gets 0.
 */
export function pvpPayoutXp(
  seat: Seat,
  winner: Seat | null,
  winReason: string | null,
): number {
  if (winner === null) return PVP_LOSS_XP
  if (seat === winner) return PVP_WIN_XP
  return isQuitReason(winReason) ? 0 : PVP_LOSS_XP
}

/**
 * Casual-queue XP for one human seat. Only a finished win pays.
 */
export function casualPayoutXp(seat: Seat, winner: Seat | null): number {
  if (winner !== null && seat === winner) return CASUAL_WIN_XP
  return 0
}

/**
 * Vs-AI XP. Surrender / leave is 0. Master pays the most among CPU levels.
 * Every CPU result pays less than a finished ranked game, win or loss.
 */
export function aiPayoutXp(input: AiPayoutInput): number {
  if (input.surrendered) return 0
  if (input.difficulty === 'test') return 0
  if (input.outcome === 'win') {
    if (input.difficulty === 'master') return AI_MASTER_WIN_XP
    if (input.difficulty === 'expert') return AI_EXPERT_WIN_XP
    if (input.difficulty === 'hard') return AI_HARD_WIN_XP
    if (input.difficulty === 'easy') return AI_EASY_WIN_XP
    return AI_NORMAL_WIN_XP
  }
  return AI_LOSS_XP
}
